import { Fragment, useState, useMemo } from "react";
import { useParams } from "react-router";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { ReactTableDataGrid } from "components/Table";
import useCallApi from "hook/useCallApi";
import useInViewport from "hook/useInViewport";
import { getProductDetailVideo } from "actions/spyAdvancedActions";
import cx from "classnames";
import { heightRow } from "../../../constant";
import useColumnTableVideosByShop from "../../../hooks/useColumnTableVideosByShop";

const limit = 20;

const sorts = [
    { label: "views", value: "views_count" },
    { label: "Likes", value: "digg_count" },
    { label: "shares", value: "share_count" },
    { label: "Estimated Sales", value: "sales" },
    { label: "Estimated GMV", value: "gmv" },
    { label: "Publish Time", value: "publish_time" },
];

function VideoList({ getProductDetailVideo }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState({ order: "publish_time", sort: "sort" });
    const [ref, inView] = useInViewport({ threshold: 0.2, once: true });
    const params = useMemo(() => ({ ...filter, page, per_page: limit, productId: id }), [filter, page, id]);
    const { data, loading } = useCallApi(getProductDetailVideo, params, inView);
    const columns = useColumnTableVideosByShop(heightRow);

    const onTableChanged = ({ page }) => {
        if (page) setPage(page);
    };

    const handleChangeSort = (value) => {
        setPage(1);
        setFilter((prev) => {
            let sort = "desc";
            if (prev.order === value && prev.sort === "desc") sort = "asc";

            return { ...prev, order: value, sort };
        });
    };

    return (
        <Fragment>
            <div className="card mt-5 mb-5 mb-xl-10" ref={ref}>
                <div
                    className="card-header border-0 cursor-pointer"
                    role="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_account_profile_details"
                    aria-expanded="true"
                    aria-controls="kt_account_profile_details"
                >
                    <div className="card-title m-0">
                        <h3 className="fw-bold m-0">{t("Video List")}</h3>
                    </div>
                </div>
                <div className="card-body" style={{ minHeight: "400px" }}>
                    {loading ? (
                        <div className="w-100 d-flex justify-content-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only"></span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <DateRangePicker
                                    onCallback={(start, end) => {
                                        let date_start = new Date(start);
                                        date_start.setHours(0, 0, 0, 0);

                                        let date_end = new Date(end);
                                        date_end.setHours(0, 0, 0, 0);

                                        setFilter((prev) => ({
                                            ...prev,
                                            start_time: Math.floor(date_start.getTime() / 1000),
                                            end_time: Math.floor(date_end.getTime() / 1000),
                                        }));
                                    }}
                                    initialSettings={{
                                        showDropdowns: true,
                                        locale: { format: "DD/MM/YYYY" },
                                        maxDate: new Date(),
                                    }}
                                >
                                    <input type="text" className="form-control al-form-control-daterangepicker" />
                                </DateRangePicker>
                            </div>
                            <div className="d-flex bg-light p-4 gap-8 text-muted">
                                <div>Sort: </div>
                                {sorts.map(({ label, value }, key) => (
                                    <div
                                        key={key}
                                        className={cx(
                                            "d-flex justify-content-center align-items-center gap-1 al-cursor-pointer",
                                            { "text-info": value === filter.order }
                                        )}
                                        onClick={() => handleChangeSort(value)}
                                    >
                                        {label}
                                        {Boolean(filter?.sort) &&
                                            filter.order === value &&
                                            (filter.sort === "asc" ? (
                                                <i
                                                    className={cx("ki-duotone ki-black-up", {
                                                        "text-info": value === filter.order,
                                                    })}
                                                ></i>
                                            ) : (
                                                <i
                                                    className={cx("ki-duotone ki-black-down", {
                                                        "text-info": value === filter.order,
                                                    })}
                                                ></i>
                                            ))}
                                    </div>
                                ))}
                            </div>
                            <ReactTableDataGrid
                                tableName="ProductVideoList"
                                idProperty="_id"
                                columns={columns}
                                rowData={data?.data || []}
                                pageNumber={page}
                                gridOptions={{
                                    rowHeight: heightRow,
                                    reactiveCustomComponents: true,
                                }}
                                paginationPageSize={limit}
                                totalSize={data?.meta?.total || 0}
                                emptyText={"No data!"}
                                isSearchText={false}
                                onTableChanged={onTableChanged}
                                height="calc(100vh - 300px)"
                                showAllColumns={true}
                            />
                        </>
                    )}
                </div>
            </div>
        </Fragment>
    );
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = {
    getProductDetailVideo,
};

export default connect(mapStateToProps, mapDispatchToProps)(VideoList);
